import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import DateInput from "../../../../parts/inputs/date-input";
import ImageUploadInput from "../../../../parts/inputs/image-upload";
import Select from "../../../../parts/inputs/select";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import {
  SPLIT_FORCED,
  SPLIT_SUGGESTED,
} from "../../../../utils/flexible-program";

// Select takes { text, value } - `name` renders a blank summary.
const WORKOUTS_PER_WEEK = Array.from({ length: 7 }, (_, i) => ({
  text: String(i + 1),
  value: i + 1,
}));

/**
 * Starts a Flexible Program for one client.
 *
 * Two things need saying before the coach commits:
 *  - a client can hold one program of this kind at a time, so starting this
 *    takes them off any team they are on (that is what `confirmMove` asks)
 *  - it LAYERS ON TOP of whatever is already on their calendar rather than
 *    replacing it, which is not what "start a program" usually implies
 *
 * A real `<form encType="multipart/form-data">`, not the usual submit-form
 * helper: that helper flattens its body into hidden textareas, which cannot
 * carry a File the thumbnail picker produces. The form wraps the whole Modal,
 * exactly like parts/modals/add-cycle.js does for the same reason - Modal's
 * own layout doesn't care what element contains it. DateInput and Select
 * already render their own named native inputs, so they need no change to
 * participate; only the plain text/file fields below needed a `name` added.
 * First day of week carries its own manual hidden input instead of a Select:
 * it's derived from startDate and can't be picked independently, and a
 * disabled Select still shows a chevron that reads as clickable, so this
 * shows the derived weekday as plain text with no input affordance at all.
 *
 * Deliberately shorter than add-cycle.js's own form: no description, no
 * intro video - starting a flexible program is meant to be quick. Neither
 * field is editable anywhere in the flexible-program UI anymore
 * (FlexibleSettings.js was trimmed the same way) - whatever a program was
 * created with before this change stays stored and keeps rendering wherever
 * it already showed, there's just no surface left to set or change it from.
 * What's left is one field per row - name, the sessions/first-day/start-date
 * trio, image, training split - with no field pairing up against another to
 * fill a two-column row. Training split reuses flexible-manager.css's own
 * `.flexible-split-options` card (the settings panel's "training type"
 * control) rather than add-cycle.js's switch-based one, stacked instead of
 * side-by-side (see the scoped override in flexible-settings-modal.css)
 * since there's no cycle yet for a real radio group to navigate against -
 * selection is plain client state.
 */
const StartFlexibleProgram = ({ locals }) => {
  const { t } = useTranslation("athlete-portal");
  const athlete = locals?.athlete;
  const onATeam = (locals?.athleteTeam || []).length > 0;
  const hasPrograms = (locals?.athleteCycles || []).length > 0;

  const [name, setName] = useState(
    `${athlete?.name || ""} - ${t("flexible.defaultProgramName")}`
  );
  const [sessionsPerWeek, setSessionsPerWeek] = useState(3);
  // Same relationship as starting a team (add-cycle.js): the coach picks a
  // calendar date, and firstDayOfWeek is READ from its weekday, not picked
  // independently - so the two can never disagree.
  const [startDate, setStartDate] = useState(() =>
    moment().add(1, "day").toDate()
  );
  const firstDayOfWeek = moment(startDate).day();
  const [trainingSplitType, setTrainingSplitType] = useState(SPLIT_SUGGESTED);

  return (
    <form
      action="/actions/cycle/flexible/start"
      method="POST"
      encType="multipart/form-data"
      data-noreset
    >
      <Modal
        toClear={[]}
        className="start-flexible-modal"
        title={t("flexible.startTitle")}
      >
        <div className="body flex flex-col gap-4">
          <p className="text-[0.75rem] opacity-70">
            {t("flexible.startIntro")}
          </p>
          {hasPrograms && (
            <p className="text-[0.75rem] flexible-notice">
              {t("flexible.layersOnTop")}
            </p>
          )}
          {onATeam && (
            <p className="text-[0.75rem] flexible-warning">
              {t("flexible.willLeaveTeam", { name: athlete?.name })}
            </p>
          )}

          <div className="group">
            <label htmlFor="flexible-program-name">
              {t("flexible.programName")}
            </label>
            <input
              id="flexible-program-name"
              name="name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="group">
              <div className="flex flex-row items-center gap-1 mb-1">
                <label className="mb-0!">{t("flexible.workoutsPerWeek")}</label>
                <ToolTip
                  body={t("flexible.workoutsPerWeekHint")}
                  id="flexible-start-sessions-info"
                >
                  <SvgIcon
                    alt={t("flexible.infoIconAlt")}
                    src="/img/new-icons/question-circle.svg"
                  />
                </ToolTip>
              </div>
              <Select
                name="sessionsPerWeek"
                options={WORKOUTS_PER_WEEK}
                value={sessionsPerWeek}
                onChange={(value) => setSessionsPerWeek(Number(value))}
                overlay={true}
              />
            </div>

            <div className="group">
              <div className="flex flex-row items-center gap-1 mb-1">
                <label className="mb-0!" htmlFor="flexible-start-date">
                  {t("flexible.startDate")}
                </label>
                <ToolTip
                  body={t("flexible.startDateHint")}
                  id="flexible-start-date-info"
                >
                  <SvgIcon
                    alt={t("flexible.infoIconAlt")}
                    src="/img/new-icons/question-circle.svg"
                  />
                </ToolTip>
              </div>
              <DateInput
                id="flexible-start-date"
                name="startDate"
                datePlaceholder={t("flexible.startDatePlaceholder")}
                value={startDate}
                onChange={(date) => setStartDate(moment(date).toDate())}
              />
            </div>

            <div className="group">
              <div className="flex flex-row items-center gap-1 mb-1">
                <label className="mb-0!">{t("flexible.firstDayOfWeek")}</label>
                <ToolTip
                  body={t("flexible.firstDayOfWeekHint")}
                  id="flexible-start-first-day-info"
                >
                  <SvgIcon
                    alt={t("flexible.infoIconAlt")}
                    src="/img/new-icons/question-circle.svg"
                  />
                </ToolTip>
              </div>
              <p className="static-field">
                {moment().day(firstDayOfWeek).format("dddd")}
              </p>
              <input
                type="hidden"
                name="firstDayOfWeek"
                value={firstDayOfWeek}
              />
            </div>
          </div>

          <div className="group">
            <ImageUploadInput name="image" className="program-image" raw />
          </div>

          {/* training split - same radio-card control as the Program
              Manager's own training-type selector (FlexibleProgramManager.js
              - .flexible-split-options/.flexible-split-option, stacked here
              instead of side by side, see flexible-settings-modal.css), not
              add-cycle.js's switch-based one. No cycle exists yet to
              navigate against, so selection is plain client state behind a
              native <label>/<input type="radio"> pair rather than that
              component's <a role="radio"> (real navigation needs a real
              link; a controlled radio needs no such workaround) - the hidden
              input is what actually carries the value on submit. */}
          <div className="group">
            <input
              type="hidden"
              name="trainingSplitType"
              value={trainingSplitType}
            />
            <div className="flexible-split-options">
              {[SPLIT_SUGGESTED, SPLIT_FORCED].map((mode) => {
                const active = trainingSplitType === mode;
                return (
                  <label
                    key={mode}
                    className="flexible-split-option mb-0! min-h-[60px]!"
                  >
                    <div className="input-wrapper">
                      <input
                        type="radio"
                        checked={active}
                        onChange={() => setTrainingSplitType(mode)}
                      />
                      <span className="radio-input">
                        <span className="radio" />
                      </span>
                    </div>
                    <span className="flexible-split-option-text">
                      <span className="flex items-center gap-2">
                        <span className="font-medium">
                          {t(`flexible.split.${mode}`)}
                        </span>
                        {mode === SPLIT_SUGGESTED && (
                          <span className="text-xs px-2! py-0.5! rounded-full bg-(--white-10) text-(--white-70)">
                            {t("flexible.default")}
                          </span>
                        )}
                      </span>
                      <span className="text-[0.75rem] opacity-70">
                        {t(`flexible.splitDesc.${mode}`)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <input type="hidden" name="clientId" value={athlete?._id || ""} />
          {locals?.query?.organizationCoach ? (
            <input
              type="hidden"
              name="organizationCoach"
              value={locals.query.organizationCoach}
            />
          ) : null}
          {/* The coach has just been told this takes the client off any
              team, so acknowledge it on their behalf. */}
          <input type="hidden" name="confirmMove" value="true" />
        </div>

        <footer>
          <button
            type="button"
            data-button-type="secondary"
            onClick={() => {
              submit("/actions/refresh", {
                method: "GET",
                body: { "modal[type]": "" },
              });
            }}
          >
            {t("flexible.cancel")}
          </button>
          <button type="submit" data-button-type="primary">
            {t("flexible.start")}
          </button>
        </footer>
      </Modal>
    </form>
  );
};

export default StartFlexibleProgram;
